import { FetchRequest } from '@rails/request.js';

const DWELL_DELAY = 2000;
// A lapsed session answers the JSON post with a bare 401, never a redirect to sign-in.
const RETRYABLE_STATUSES = new Set([401, 403]);

/**
 * Clears a client's New/Updated badge for a design they actually looked at. Shared by both
 * presentation carousels: each decides when a design is really on screen, this decides how
 * long a look has to last and records it once.
 */
export default class DesignVisitRecorder {
  constructor(url) {
    this.url = url;
    this.recorded = new Set();
  }

  schedule(designType, designId) {
    this.cancel();

    if (!designType || !designId) {
      return;
    }

    this.timer = window.setTimeout(() => this.#record(designType, designId), DWELL_DELAY);
  }

  cancel() {
    window.clearTimeout(this.timer);
  }

  #record(designType, designId) {
    const key = `${designType}-${designId}`;
    if (this.recorded.has(key)) {
      return;
    }
    // Claimed before the request so a slow one isn't sent twice.
    this.recorded.add(key);

    new FetchRequest('post', this.url, {
      body: { design_type: designType, design_id: designId },
      responseKind: 'json',
      // A redirect means we never reached the endpoint, so reject instead of reading it as a
      // reply: the catch below then releases the key like any other failure.
      redirect: 'error',
    })
      .perform()
      .then((response) => {
        // Nothing was written, so release the key and let a later dwell retry it. Any other
        // 4xx is permanent and stays claimed.
        if (response.statusCode >= 500 || RETRYABLE_STATUSES.has(response.statusCode)) {
          this.recorded.delete(key);
        }
      })
      .catch(() => this.recorded.delete(key));
  }
}
